import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, Shield, Clock, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useSeo, { useJsonLd } from "@/lib/useSeo";
import { BRAND } from "@/lib/brand";

/**
 * Reusable SEO service landing page.
 * All content is passed in as props so each service (car-transport, vehicle-recovery,
 * non-runner-transport, nationwide-vehicle-transport) has genuinely unique copy.
 */
export default function ServicePage({
  slug,
  title,        // <title>
  metaDescription,
  h1,
  intro,
  bullets = [],
  sections = [], // [{title, body}]
  faq = [],      // [{q, a}]
  heroImage,
  testid,
}) {
  const canonical = `https://mhjprestigetransport.com/${slug}`;

  useSeo({ title, description: metaDescription, canonical });
  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": h1,
      "provider": { "@id": "https://mhjprestigetransport.com/#business" },
      "areaServed": { "@type": "Country", "name": "United Kingdom" },
      "description": metaDescription,
      "url": canonical,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://mhjprestigetransport.com/quote",
        "priceCurrency": "GBP",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "GBP",
          "description": "Instant online quote available"
        }
      }
    },
    `service-${slug}`
  );

  useJsonLd(
    faq.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faq.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }
      : null,
    `faq-${slug}`
  );

  return (
    <main data-testid={testid} className="bg-[#050505] text-white min-h-screen">
      <Header />
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        {heroImage && (
          <div className="absolute inset-0">
            <img src={heroImage} alt={h1} className="w-full h-full object-cover opacity-40" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          </div>
        )}
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-6">
            MHJ Prestige Transport · Service
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl"
          >
            {h1}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-zinc-300 text-base lg:text-lg leading-relaxed">
            {intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/quote" data-testid={`${testid}-cta-quote`} className="btn-primary">
              Get an Instant Quote <ArrowUpRight className="w-5 h-5" />
            </a>
            <a href={`tel:${BRAND.phoneRaw}`} data-testid={`${testid}-cta-call`} className="btn-ghost">
              <Phone className="w-5 h-5" /> Call {BRAND.phone}
            </a>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <TrustCell icon={<Clock className="w-4 h-4" />} label="Instant online quote" sub="No phone calls needed" />
            <TrustCell icon={<Shield className="w-4 h-4" />} label="Fully insured" sub="Goods in transit" />
            <TrustCell icon={<MapPin className="w-4 h-4" />} label="Nationwide UK" sub="Burton based" />
            <TrustCell icon={<Check className="w-4 h-4" />} label="Book online" sub="Secure Stripe deposit" />
          </div>
        </div>
      </section>

      {bullets.length > 0 && (
        <section className="py-16 md:py-20 border-t border-white/5">
          <div className="max-w-[1300px] mx-auto px-6 md:px-12">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
              What&rsquo;s included
            </div>
            <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-tight max-w-3xl">
              Why customers choose MHJ for this.
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 mt-10 max-w-4xl">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-300">
                  <Check className="w-5 h-5 text-[#E60000] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {sections.map((s, i) => (
        <section key={i} className="py-14 md:py-20 border-t border-white/5">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <h2 className="font-display font-extrabold text-white text-2xl md:text-4xl leading-tight">{s.title}</h2>
            <div className="mt-5 text-zinc-300 text-base leading-relaxed space-y-4 max-w-3xl">
              {s.body}
            </div>
          </div>
        </section>
      ))}

      {faq.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">FAQ</div>
            <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl mb-10">Common questions.</h2>
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {faq.map((f, i) => (
                <li key={i} className="py-6" data-testid={`faq-item-${i}`}>
                  <div className="font-display font-bold text-white text-lg md:text-xl">{f.q}</div>
                  <p className="text-zinc-400 mt-2 text-base leading-relaxed">{f.a}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 border-t border-white/5 bg-[#080808]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl">
            Ready to book?
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Get an instant online quote in under 60 seconds — no phone call required.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="/quote" className="btn-primary">
              Get an Instant Quote <ArrowUpRight className="w-5 h-5" />
            </a>
            <a href={`tel:${BRAND.phoneRaw}`} className="btn-ghost">
              <Phone className="w-5 h-5" /> Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function TrustCell({ icon, label, sub }) {
  return (
    <div className="bg-black/70 backdrop-blur-sm px-5 py-5 flex items-start gap-3">
      <div className="text-[#E60000] mt-0.5">{icon}</div>
      <div>
        <div className="text-white text-sm font-bold tracking-wide">{label}</div>
        <div className="text-zinc-500 text-xs mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
